import React, {useEffect, useState, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import AppComponentHeader from '../../../@crema/core/AppComponentHeader';
import AppGridContainer from '../../../@crema/core/AppGridContainer';
import Grid from '@mui/material/Grid';
import {Button, CircularProgress, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TablePagination, Chip, Box, Skeleton, Card, CardContent, Typography, IconButton} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import FirstPageIcon from '@mui/icons-material/FirstPage';
import LastPageIcon from '@mui/icons-material/LastPage';
import KeyboardArrowLeft from '@mui/icons-material/KeyboardArrowLeft';
import KeyboardArrowRight from '@mui/icons-material/KeyboardArrowRight';
import {BiExport} from 'react-icons/bi';
import FilterCardSendData from 'components/FilterCard/FilterCardSendData';
import jwtAxios from '@crema/services/auth/jwt-auth';
import {useDispatch} from 'react-redux';
import {fetchError, showMessage} from 'redux/actions';
import { formatDateToJerusalem } from '../../../utils/dateFormatter';

const normalizeToDayIso = (d, isEndOfDay = false) => {
  if (!d) return '';
  try {
    const dd = d instanceof Date ? new Date(d) : new Date(d);
    if (isNaN(dd.getTime())) return '';
    if (isEndOfDay) dd.setHours(23, 59, 59, 999); else dd.setHours(0, 0, 0, 0);
    return dd.toISOString();
  } catch {
    return '';
  }
};

// Custom pagination actions component that disables next button on last page
const CustomTablePaginationActions = ({count, page, rowsPerPage, onPageChange}) => {
  const handleFirstPageButtonClick = (event) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (event) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (event) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (event) => {
    const maxPage = count > 0 ? Math.max(0, Math.floor((count - 1) / rowsPerPage)) : 0;
    onPageChange(event, maxPage);
  };

  const maxPage = count > 0 ? Math.max(0, Math.floor((count - 1) / rowsPerPage)) : 0;
  const isLastPage = page >= maxPage;

  return (
    <Box sx={{flexShrink: 0, ml: 2.5}}>
      <IconButton onClick={handleFirstPageButtonClick} disabled={page === 0} aria-label='first page'>
        <FirstPageIcon />
      </IconButton>
      <IconButton onClick={handleBackButtonClick} disabled={page === 0} aria-label='previous page'>
        <KeyboardArrowLeft />
      </IconButton>
      <IconButton onClick={handleNextButtonClick} disabled={isLastPage || count === 0} aria-label='next page'>
        <KeyboardArrowRight />
      </IconButton>
      <IconButton onClick={handleLastPageButtonClick} disabled={isLastPage || count === 0} aria-label='last page'>
        <LastPageIcon />
      </IconButton>
    </Box>
  );
};

CustomTablePaginationActions.propTypes = {
  count: PropTypes.number.isRequired,
  page: PropTypes.number.isRequired,
  rowsPerPage: PropTypes.number.isRequired,
  onPageChange: PropTypes.func.isRequired,
};

const CustomerCrossCheckReport = () => {
  const dispatch = useDispatch();
  const [isFilterCardVisible, setIsFilterCardVisible] = useState(true);
  const [loading, setLoading] = useState(false);
  const [filtersApplied, setFiltersApplied] = useState(false);

  const [filters, setFilters] = useState({
    dateRange: [null, null],
    sentDateRange: [null, null],
    domain: '',
    customer: '',
    country: '',
    dataSource: '',
  });

  const [domains, setDomains] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [rows, setRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(100);
  const [totalCount, setTotalCount] = useState(0);
  const [totalSentRecords, setTotalSentRecords] = useState(0);
  const isCorrectingPageRef = useRef(false);

  const toggleFilterCardVisibility = () => {
    setIsFilterCardVisible(!isFilterCardVisible);
  };

  // Load filter options (reuse Data page endpoints)
  useEffect(() => {
    let isMounted = true;
    // domains via search endpoint and map to {label,key}
    jwtAxios
      .get('domain/search?pageSize=50&pageNumber=0&term=')
      .then((resp) => {
        if (!isMounted) return;
        const list = Array.isArray(resp?.data?.data)
          ? resp.data.data.map((item) => ({
              label: item.domain_name,
              key: item.id,
              url: item.domain_backend_url,
            }))
          : [];
        setDomains(list);
      })
      .catch((e) => dispatch(fetchError(e.message)));

    // customers first page
    jwtAxios
      .get('customer?pageSize=50&pageNumber=0')
      .then((resp) => {
        if (!isMounted) return;
        const data = resp?.data?.data?.data || [];
        const list = data
          .filter((item) => item && (item.user_name || item.name) && item.id != null)
          .map((item) => ({label: item.user_name || item.name, key: item.id}));
        setCustomers(list);
      })
      .catch((e) => dispatch(fetchError(e.message)));

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const handleDateRangeChange = (value) => {
    setFiltersApplied(false);
    setFilters((f) => ({...f, dateRange: value}));
  };
  const handleSentDateRangeChange = (value) => {
    setFiltersApplied(false);
    setFilters((f) => ({...f, sentDateRange: value}));
  };
  const handleDomainChange = async (_event, valueSelected) => {
    const domainId = valueSelected?.key || '';
    setFiltersApplied(false);
    setFilters((prev) => ({...prev, domain: domainId, customer: ''}));
    // Fetch customers filtered by domain
    try {
      const url = domainId
        ? `customer/search?pageSize=50&pageNumber=0&term=&domainId=${domainId}`
        : `customer?pageSize=50&pageNumber=0`;
      const resp = await jwtAxios.get(url);
      const data = resp?.data?.data?.data || [];
      const list = data
        .filter((item) => item && (item.user_name || item.name) && item.id != null)
        .map((item) => ({label: item.user_name || item.name, key: item.id}));
      setCustomers(list);
    } catch (e) {
      dispatch(fetchError(e?.message || 'Failed to load customers'));
    }
  };
  const handleCustomerChange = (_event, valueSelected) => {
    setFiltersApplied(false);
    setFilters((prev) => ({...prev, customer: valueSelected?.key || ''}));
  };

  const buildPayload = () => {
    const [startDate, endDate] = filters.dateRange || [];
    const fromDate = normalizeToDayIso(startDate, false);
    const toDate = normalizeToDayIso(endDate, true);
    const [sentStart, sentEnd] = filters.sentDateRange || [];
    const sentFromDate = normalizeToDayIso(sentStart, false);
    const sentToDate = normalizeToDayIso(sentEnd, true);
    return {
      dataSource: filters.dataSource || '',
      country: filters.country || '',
      domainId: filters.domain || '',
      customerId: filters.customer || '',
      fromDate: fromDate || '',
      toDate: toDate || '',
      sentFromDate: sentFromDate || '',
      sentToDate: sentToDate || '',
    };
  };

  const onApply = () => {
    dispatch(showMessage('Filters applied'));
    setFiltersApplied(true);
    // Fetch total sent records (all leads, not filtered)
    fetchTotalSentRecords();
    // Fetch list preview matching filters
    fetchList();
  };

  const handleClearFilters = () => {
    setFilters({dateRange: [null, null], sentDateRange: [null, null], domain: '', customer: '', country: '', dataSource: ''});
    setFiltersApplied(false);
    setTotalSentRecords(0);
    setTotalCount(0);
  };

  const handleGenerate = async () => {
    try {
      setLoading(true);
      // Export all filtered leads (not paginated) based on applied filters
      const payload = buildPayload();
      // Prefer URL response; fallback to blob
      const resp = await jwtAxios.post('/reports/customer-cross-check/export', payload, {responseType: 'json'}).catch(() => null);
      if (resp && resp.data && (resp.data.fileUrl || resp.data.data)) {
        if (resp.data.fileUrl) {
          window.open(resp.data.fileUrl, '_blank');
          dispatch(showMessage('Report is downloading'));
        } else if (resp.data.data && resp.data.fileName) {
          const byteCharacters = atob(resp.data.data);
          const byteNumbers = new Uint8Array(byteCharacters.length);
          for (let i = 0; i < byteCharacters.length; i++) byteNumbers[i] = byteCharacters.charCodeAt(i);
          const blob = new Blob([byteNumbers], {type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'});
          const url = window.URL.createObjectURL(blob);
          const link = document.createElement('a');
          link.href = url;
          link.setAttribute('download', resp.data.fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          dispatch(showMessage('Report downloaded'));
        } else {
          dispatch(fetchError('Unexpected export response'));
        }
        return;
      }
      // If JSON path failed, try blob
      const blobResp = await jwtAxios.post('/reports/customer-cross-check/export', buildPayload(), {responseType: 'blob'});
      const url = window.URL.createObjectURL(new Blob([blobResp.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'customer_cross_check_report.xlsx');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      dispatch(showMessage('Report downloaded'));
    } catch (e) {
      dispatch(fetchError(e?.message || 'Failed to generate report'));
    } finally {
      setLoading(false);
    }
  };

  const fetchList = useCallback(async () => {
    try {
      setListLoading(true);
      // Build query like Data page single-customer endpoint
      const [startDate, endDate] = filters.dateRange || [];
      const from = normalizeToDayIso(startDate, false);
      const to = normalizeToDayIso(endDate, true);
      const [sentStart, sentEnd] = filters.sentDateRange || [];
      const sentFrom = normalizeToDayIso(sentStart, false);
      const sentTo = normalizeToDayIso(sentEnd, true);
      let query = `data/sent-to-single-customer?pageNumber=${page}&pageSize=${rowsPerPage}`;
      if (filters.customer) query += `&customer=${encodeURIComponent(filters.customer)}`;
      if (filters.country) query += `&country=${encodeURIComponent(filters.country)}`;
      if (filters.domain) query += `&domainId=${encodeURIComponent(filters.domain)}`;
      if (filters.dataSource) query += `&dataSource=${encodeURIComponent(filters.dataSource)}`;
      if (from && to) query += `&fromDate=${encodeURIComponent(from)}&toDate=${encodeURIComponent(to)}`;
      if (sentFrom && sentTo) query += `&sentFromDate=${encodeURIComponent(sentFrom)}&sentToDate=${encodeURIComponent(sentTo)}`;

      const result = await jwtAxios.get(query);
      const list = result?.data?.data?.data || [];
      const count = result?.data?.data?.count ?? 0;
      
      setRows(list);
      setTotalCount(count);
      
      // Fix pagination: if current page is beyond available data, reset to last valid page
      if (count > 0) {
        const maxPage = Math.max(0, Math.floor((count - 1) / rowsPerPage));
        if (page > maxPage) {
          isCorrectingPageRef.current = true;
          setPage(maxPage);
        }
      } else if (count === 0 && page > 0) {
        // If no data, reset to page 0
        setPage(0);
      }
    } catch (e) {
      dispatch(fetchError(e?.message || 'Failed to fetch data'));
    } finally {
      setListLoading(false);
    }
  }, [filters, page, rowsPerPage, dispatch]);

  const fetchTotalSentRecords = useCallback(async () => {
    try {
      // Fetch total count without filters (except customer if selected)
      let query = `data/sent-to-single-customer?pageNumber=0&pageSize=1`;
      if (filters.customer) query += `&customer=${encodeURIComponent(filters.customer)}`;

      const result = await jwtAxios.get(query);
      const count = result?.data?.data?.count ?? 0;
      setTotalSentRecords(count);
    } catch (e) {
      // Silently fail - total count is optional
      console.error('Failed to fetch total sent records:', e);
    }
  }, [filters.customer]);

  const handleChangePage = (_e, newPage) => {
    // Validate page number against total count before setting
    if (totalCount > 0) {
      const maxPage = Math.max(0, Math.floor((totalCount - 1) / rowsPerPage));
      const validPage = Math.min(newPage, maxPage);
      setPage(validPage);
      if (filtersApplied && validPage === newPage) {
        // Only fetch if the page is valid (not corrected)
        fetchList();
      } else if (filtersApplied) {
        // If page was corrected, wait for state update then fetch
        setTimeout(() => {
          fetchList();
        }, 0);
      }
    } else {
      setPage(newPage);
      if (filtersApplied) {
        fetchList();
      }
    }
  };
  
  // Fix pagination: when totalCount or rowsPerPage changes, ensure page is within valid range
  useEffect(() => {
    if (totalCount > 0 && filtersApplied && !isCorrectingPageRef.current) {
      const maxPage = Math.max(0, Math.floor((totalCount - 1) / rowsPerPage));
      if (page > maxPage) {
        isCorrectingPageRef.current = true;
        setPage(maxPage);
      }
    }
  }, [totalCount, rowsPerPage, filtersApplied, page]); // Include page to detect when it's corrected
  
  // When page is corrected, fetch the data
  useEffect(() => {
    if (isCorrectingPageRef.current && filtersApplied) {
      isCorrectingPageRef.current = false;
      fetchList();
    }
  }, [page, filtersApplied, fetchList]);
  const handleChangeRowsPerPage = (e) => {
    setRowsPerPage(parseInt(e.target.value, 10));
    setPage(0);
    if (filtersApplied) fetchList();
  };

  const formatDate = (value) => {
    return formatDateToJerusalem(value);
  };

  return (
    <>
      <AppComponentHeader title='Customer Cross Check Report' />
      <AppGridContainer>
        <Grid item xs={12}>
          <Button variant='contained' startIcon={<FilterListIcon />} onClick={toggleFilterCardVisibility} sx={{mr: 4}}>
            {isFilterCardVisible ? 'Hide Filters' : 'Show Filters'}
          </Button>
          <Button variant='contained' startIcon={<BiExport />} onClick={handleGenerate} disabled={loading || !filtersApplied}>
            {loading ? <CircularProgress size={18} sx={{color: '#fff'}} /> : 'Generate Report'}
          </Button>
        </Grid>

        {isFilterCardVisible && (
          <Grid item xs={12}>
            <FilterCardSendData
              domains={domains}
              customers={customers}
              filters={filters}
              setFilters={setFilters}
              handleDateRangeChange={handleDateRangeChange}
              handleSentDateRangeChange={handleSentDateRangeChange}
              onApply={onApply}
              onClear={handleClearFilters}
              handleDomainChange={handleDomainChange}
              handleCustomerChange={handleCustomerChange}
              disableDataSource={false}
            />
          </Grid>
        )}

        {/* Summary card */}
        <Grid item xs={12}>
          <Card variant='outlined'>
            <CardContent>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1}}>
                <Typography variant='subtitle1'>Total sent records</Typography>
                <Typography variant='h6'>{listLoading ? '...' : totalSentRecords}</Typography>
              </Box>
              <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
                <Typography variant='body2' color='text.secondary'>Number of filtered leads</Typography>
                <Typography variant='body1' fontWeight='medium'>{listLoading ? '...' : totalCount}</Typography>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        {/* List Preview */}
        {filtersApplied && (
          <Grid item xs={12}>
            <TableContainer 
              component={Paper} 
              sx={{
                height: listLoading 
                  ? `calc(48px + ${10 * 36}px)` 
                  : rows.length > 0 
                    ? `calc(48px + ${rows.length * 36}px)` 
                    : 'calc(48px + 216px)',
                maxHeight: listLoading 
                  ? `calc(48px + ${10 * 36}px)` 
                  : rows.length > 0 
                    ? `calc(48px + ${rows.length * 36}px)` 
                    : 'calc(48px + 216px)',
                borderRadius: 2,
                overflow: 'auto'
              }}
            >
            <Table size='small' stickyHeader>
              <TableHead sx={{backgroundColor: 'white'}}>
                <TableRow sx={{'& th': {fontWeight: 600, backgroundColor: 'white'}}}>
                  <TableCell width={90} sx={{backgroundColor: 'white'}}>ID</TableCell>
                  <TableCell width={180} sx={{backgroundColor: 'white'}}>Full Name</TableCell>
                  <TableCell width={140} sx={{backgroundColor: 'white'}}>Phone</TableCell>
                  <TableCell width={220} sx={{backgroundColor: 'white'}}>Email</TableCell>
                  <TableCell width={120} sx={{backgroundColor: 'white'}}>Country</TableCell>
                  <TableCell width={160} sx={{backgroundColor: 'white'}}>Data Source</TableCell>
                  <TableCell width={200} sx={{backgroundColor: 'white'}}>Customer</TableCell>
                  <TableCell width={190} sx={{backgroundColor: 'white'}}>Created At</TableCell>
                  <TableCell width={190} sx={{backgroundColor: 'white'}}>Sent At</TableCell>
                  <TableCell width={120} align='center' sx={{backgroundColor: 'white'}}>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {listLoading ? (
                  Array.from({length: 10}).map((_, i) => (
                    <TableRow key={`s-${i}`}>
                      {Array.from({length: 10}).map((__, c) => (
                        <TableCell key={`s-${i}-${c}`} sx={{py: 0}}>
                          <Skeleton variant='rectangular' height={36} sx={{borderRadius: 1}} />
                        </TableCell>
                      ))}
                    </TableRow>
                  ))
                ) : rows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={10} align='center' sx={{py: 6, color: 'text.secondary'}}>
                      No data found. Adjust filters and click Apply.
                    </TableCell>
                  </TableRow>
                ) : (
                  rows.map((r, idx) => (
                    <TableRow key={r.id || idx} hover sx={{'&:nth-of-type(odd)': {backgroundColor: 'action.hover'}}}>
                      <TableCell>{r.id}</TableCell>
                      <TableCell>{r.fullName}</TableCell>
                      <TableCell>{r.phone}</TableCell>
                      <TableCell>{r.email}</TableCell>
                      <TableCell>{r.country}</TableCell>
                      <TableCell>{r.data_source}</TableCell>
                      <TableCell>{r.customer_name}</TableCell>
                      <TableCell>{formatDate(r.created_at)}</TableCell>
                      <TableCell>{formatDate(r.sent_at)}</TableCell>
                      <TableCell align='center'>
                        <Chip label='Success' color='success' size='small' variant='outlined' />
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          <TablePagination
              component='div'
              count={totalCount}
              rowsPerPageOptions={[25, 50, 100]}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              labelDisplayedRows={() => {
                if (totalCount === 0) return '0-0';
                const actualFrom = page * rowsPerPage + 1;
                const actualTo = Math.min((page + 1) * rowsPerPage, totalCount);
                return `${actualFrom}-${actualTo} of ${totalCount}`;
              }}
              ActionsComponent={CustomTablePaginationActions}
            />
          </Grid>
        )}
      </AppGridContainer>
    </>
  );
};

export default CustomerCrossCheckReport;


